<template>
  <div class="flex flex-col bg-inherit">
    <div class="relative mt-2 flex flex-col bg-inherit" @click="focusDraftInput">
      <div
        class="peer rounded-extra-small text-body-large relative z-0 box-border flex max-h-48 min-h-14 w-full cursor-text flex-wrap items-center gap-1 overflow-y-auto border px-2 py-2 outline-none"
        :class="[
          error && 'border-error hover:border-on-error-container focus-within:border-error',
          !error && 'border-outline focus-within:border-primary',
          isDraftFocused && 'border-[3px] px-1.5 py-1.5',
        ]"
      >
        <BaseChip
          v-for="(member, index) in members"
          :key="`${member.firstName}-${member.lastName ?? ''}-${index}`"
          icon="person"
          type="input"
          class="group relative max-w-full overflow-hidden"
          @click.stop="removeMember(index)"
        >
          <span class="truncate">{{ chipLabel(member) }}</span>
          <BaseStateLayer />
        </BaseChip>
        <input
          ref="draftInput"
          v-model="nameDraft"
          type="text"
          class="text-body-large text-on-surface min-w-32 grow border-none bg-transparent outline-none placeholder:text-transparent"
          :placeholder="label"
          @keydown="onDraftKeydown"
          @input="onDraftInput"
          @blur="onDraftBlur"
          @focus="isDraftFocused = true"
          @paste="onDraftPaste"
        />
      </div>
      <label
        class="pointer-events-none absolute left-3 bg-inherit px-1 transition-all"
        :class="[
          error && 'text-error',
          !error && (isDraftFocused ? 'text-primary' : 'text-on-surface-variant'),
          isDraftFocused || hasContent
            ? 'text-body-small -top-2'
            : 'text-body-large top-1/2 -translate-y-1/2',
        ]"
      >
        {{ label }}{{ required ? '*' : '' }}
      </label>
    </div>
    <p v-if="error" class="text-error text-body-small mt-1 px-4">
      {{ $t(error, { field: label }) }}
    </p>
    <p v-else-if="members.length > 0" class="text-on-surface-variant text-body-small mt-1 px-4">
      {{ $t('organization.loginLessMember.count', { count: members.length }) }}
    </p>
    <p v-else-if="supportingText" class="text-on-surface-variant text-body-small mt-1 px-4">
      {{ supportingText }}
    </p>
  </div>
</template>
<script lang="ts" setup>
  import { computed, ref, useTemplateRef } from 'vue'
  import BaseChip from '../../../core/components/base/chip/BaseChip.vue'
  import BaseStateLayer from '../../../core/components/base/state-layer/BaseStateLayer.vue'
  import type { LoginLessMemberName } from '../composables/useOrganizationSettings'

  const PERSON_DELIMITER = /[,;\n]+/

  interface Props {
    label: string
    error?: string
    required?: boolean
    supportingText?: string
  }

  defineProps<Props>()

  const members = defineModel<LoginLessMemberName[]>({ required: true })

  const nameDraft = ref('')
  const isDraftFocused = ref(false)
  const draftInput = useTemplateRef<HTMLInputElement>('draftInput')

  const hasContent = computed(() => members.value.length > 0 || nameDraft.value.length > 0)

  const chipLabel = (member: LoginLessMemberName) => {
    return member.lastName ? `${member.firstName} ${member.lastName}` : member.firstName
  }

  const parsePerson = (raw: string): LoginLessMemberName | null => {
    const trimmed = raw.trim()
    if (!trimmed) return null

    const tabIndex = trimmed.indexOf('\t')
    if (tabIndex !== -1) {
      const firstName = trimmed.slice(0, tabIndex).trim()
      const lastName = trimmed.slice(tabIndex).replace(/\t+/g, ' ').trim()
      if (!firstName) return null
      return lastName ? { firstName, lastName } : { firstName }
    }

    const spaceIndex = trimmed.search(/\s/)
    if (spaceIndex === -1) {
      return { firstName: trimmed }
    }
    const firstName = trimmed.slice(0, spaceIndex).trim()
    const lastName = trimmed.slice(spaceIndex).trim()
    if (!firstName) return null
    return lastName ? { firstName, lastName } : { firstName }
  }

  const addPeople = (rawPeople: ReadonlyArray<string>) => {
    const parsed = rawPeople.flatMap((person) => {
      const member = parsePerson(person)
      return member ? [member] : []
    })
    if (parsed.length === 0) return
    members.value = [...members.value, ...parsed]
  }

  const removeMember = (index: number) => {
    members.value = members.value.filter((_, memberIndex) => memberIndex !== index)
  }

  const commitDraftParts = () => {
    if (!PERSON_DELIMITER.test(nameDraft.value)) return

    const endsWithDelimiter = /[,;\n]$/.test(nameDraft.value)
    const parts = nameDraft.value.split(PERSON_DELIMITER)
    const peopleToCommit = endsWithDelimiter
      ? parts.filter((part) => part.length > 0)
      : parts.slice(0, -1).filter((part) => part.length > 0)
    const remainder = endsWithDelimiter ? '' : (parts[parts.length - 1] ?? '')

    addPeople(peopleToCommit)
    nameDraft.value = remainder
  }

  const commitDraft = () => {
    const trimmed = nameDraft.value.trim()
    if (!trimmed) {
      nameDraft.value = ''
      return
    }
    addPeople([trimmed])
    nameDraft.value = ''
  }

  const onDraftInput = () => {
    commitDraftParts()
  }

  const onDraftKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      commitDraft()
      return
    }

    if (event.key === 'Backspace' && nameDraft.value.length === 0 && members.value.length > 0) {
      event.preventDefault()
      removeMember(members.value.length - 1)
    }
  }

  const onDraftBlur = () => {
    isDraftFocused.value = false
    commitDraft()
  }

  const onDraftPaste = (event: ClipboardEvent) => {
    const pastedText = event.clipboardData?.getData('text')
    if (!pastedText || !/[,;\n\t]/.test(pastedText)) return

    event.preventDefault()
    const parts = `${nameDraft.value}${pastedText}`
      .split(PERSON_DELIMITER)
      .map((part) => part.trim())
      .filter((part) => part.length > 0)

    addPeople(parts)
    nameDraft.value = ''
  }

  const focusDraftInput = () => {
    draftInput.value?.focus()
  }
</script>
